import { useRef, useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple, ArrowClockwise } from "@phosphor-icons/react";

/**
 * Theme snapshot — export/import the full site theme as portable JSON.
 * Lets admins back up brand config, migrate between environments,
 * or roll back a bad tweak instantly.
 */
export default function ThemeSnapshotEditor({ token, onImported }) {
  const [busy, setBusy] = useState(null); // 'export' | 'import' | null
  const fileRef = useRef(null);

  const exportSnap = async () => {
    setBusy("export");
    try {
      const { data } = await axios.get(`${API}/admin/theme/export`, { headers: { "X-Admin-Token": token } });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `wisewit-theme-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Theme snapshot downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed.");
    } finally { setBusy(null); }
  };

  const importSnap = async (file) => {
    if (!file) return;
    setBusy("import");
    try {
      const text = await file.text();
      let payload;
      try { payload = JSON.parse(text); }
      catch { toast.error("Selected file is not valid JSON."); setBusy(null); return; }
      const { data } = await axios.post(`${API}/admin/theme/import`, payload, {
        headers: { "X-Admin-Token": token },
      });
      toast.success(`Theme imported (${data.applied_fields?.length || 0} fields).`);
      if (onImported) await onImported();
      // Small refresh so all cached CSS vars re-hydrate site-wide.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border-t border-[#2A2A2A] pt-6 mt-6" data-testid="theme-snapshot-editor">
      <div className="flex items-start gap-4">
        <ArrowClockwise size={22} color="#DFFF00" weight="duotone" />
        <div className="flex-1">
          <h3 className="font-display text-xl">Theme snapshot</h3>
          <p className="mt-1 text-[#8E8E93] text-sm max-w-2xl">
            Export the entire site theme (colours, fonts, digest, footer products, contact email) as a single JSON file.
            Import it later to restore, or share it with a designer for review.
          </p>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button
              onClick={exportSnap}
              disabled={busy !== null}
              className="btn-secondary"
              data-testid="theme-export-btn"
            >
              <DownloadSimple size={14} /> {busy === "export" ? "Exporting…" : "Export snapshot (.json)"}
            </button>

            <input
              type="file"
              ref={fileRef}
              accept="application/json,.json"
              onChange={(e) => importSnap(e.target.files?.[0])}
              hidden
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy !== null}
              className="btn-ghost"
              data-testid="theme-import-btn"
            >
              <UploadSimple size={14} /> {busy === "import" ? "Importing…" : "Import snapshot"}
            </button>
          </div>
          <div className="mt-3 font-mono text-[10px] text-[#555]">
            Import replaces current theme instantly. Page will reload to apply.
          </div>
        </div>
      </div>
    </div>
  );
}
